import KanbanIcon from "@atlaskit/icon-lab/core/kanban";
import ListChecklistIcon from "@atlaskit/icon/core/list-checklist";
import TableIcon from "@atlaskit/icon/core/table";
import type {ReactNode} from "react";
import {twJoin} from "tailwind-merge";

export type ViewMode = "table" | "kanban" | "list";

const VIEW_MODES: Array<ViewMode> = ["table", "kanban", "list"];

function viewModeLabel(viewMode: ViewMode): string {
    switch (viewMode) {
        case "table": {
            return "Table";
        }
        case "kanban": {
            return "Kanban";
        }
        case "list": {
            return "List";
        }
    }
}

function viewModeIcon(viewMode: ViewMode): ReactNode {
    const iconProps = {label: "", size: "medium" as const};
    switch (viewMode) {
        case "table": {
            return <TableIcon {...iconProps} />;
        }
        case "kanban": {
            return <KanbanIcon {...iconProps} />;
        }
        case "list": {
            return <ListChecklistIcon {...iconProps} />;
        }
    }
}

/**
 * Figma view switcher: light outer border, hairline dividers, selected segment with
 * full blue outline + pale blue fill (not clipped by the container).
 */
export function ViewModeSegmentedControl({
    selectedView,
    onViewChange,
    className,
    views = VIEW_MODES,
}: {
    selectedView: ViewMode;
    onViewChange: (view: ViewMode) => void;
    className?: string;
    views?: Array<ViewMode>;
}) {
    if (views.length === 0) {
        return null;
    }

    return (
        <div
            className={twJoin(
                "inline-flex items-stretch shrink-0 p-px",
                "rounded-[8px] border border-solid border-ds-border bg-ds-elevation-surface",
                className,
            )}
            role="group"
            aria-label="View mode"
        >
            {views.map((view, index) => {
                const isSelected = view === selectedView;
                const isFirst = index === 0;
                const isLast = index === views.length - 1;
                const showDivider = isSelected !== true && index < views.length - 1 && views[index + 1] !== selectedView;

                return (
                    <button
                        key={view}
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={`${viewModeLabel(view)} view`}
                        onClick={() => {
                            onViewChange(view);
                        }}
                        className={twJoin(
                            "relative grid place-items-center size-8 shrink-0 cursor-pointer",
                            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-border-accent-blue",
                            isSelected
                                ? twJoin(
                                      "z-10 rounded-[6px] border border-solid border-ds-border-accent-blue",
                                      "bg-ds-background-accent-blue-subtlest text-ds-icon-accent-blue",
                                  )
                                : twJoin(
                                      "border border-solid border-transparent bg-transparent text-ds-icon",
                                      isFirst && "rounded-l-[6px]",
                                      isLast && "rounded-r-[6px]",
                                      showDivider &&
                                          "after:absolute after:right-0 after:top-1/2 after:h-4 after:w-px after:-translate-y-1/2 after:bg-ds-border after:content-['']",
                                  ),
                        )}
                    >
                        <span className="grid place-items-center shrink-0 text-current [&_svg]:text-current" aria-hidden>
                            {viewModeIcon(view)}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
